import { Expand } from "@mui/icons-material"
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
} from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import React, { useMemo } from "react"

import { Token } from "./EntityPortfolioAgents"
import { readHopperAgentInfo } from "@/api/agents-api"
import { getMessageById } from "@/api/messages-api"
import { DexiSpinner } from "@/components/DexiSpinner"
import { ErrorBoundary } from "@/components/ErrorBoundary"
import { IdBlock } from "@/components/IdBlock"
import { LoadingSkeletons } from "@/components/LoadingSkeletons"
import { NumberFont } from "@/components/RootLayout/fonts"
import { truncateId } from "@/utils/data-utils"
import { formatRelative } from "@/utils/format"

export function SingleHopperAgent(props: { entityId: string; agentPageView?: boolean }) {
  const { entityId, agentPageView } = props
  const [openLogs, setOpenLogs] = React.useState(false)

  const { data: message } = useQuery({
    queryKey: ["message", entityId],
    queryFn: () => {
      if (!entityId) throw new Error("Invalid params")
      return getMessageById(entityId)
    },
    enabled: !!entityId,
  })

  const { data: agentInfo } = useQuery({
    queryKey: ["agent_info", entityId],
    queryFn: async () => {
      const res = await readHopperAgentInfo(entityId)
      console.log("onAgentInfo", entityId, res)
      return res
    },
  })

  const logs = useMemo(() => {
    if (!agentInfo || !agentInfo.Logs) return []

    try {
      return JSON.parse(agentInfo.Logs)
    } catch (e) {
      return []
    }
  }, [agentInfo])

  if (!agentInfo || !message) return agentPageView ? <LoadingSkeletons /> : null

  const executing = agentInfo["IsExecutingChain"]
  const currentHop = (agentInfo?.["CurrentPathProgress"] as any)?.["CurrentHop"]
  const totalHops = (agentInfo?.["CurrentPathProgress"] as any)?.["TotalHops"]

  const detailsElement = (
    <>
      <Stack>
        <Typography variant="caption" color="secondary">
          Status
        </Typography>
        <Stack
          direction="row"
          gap={0.5}
          alignContent={"center"}
          sx={{
            "& .dexi-spinner": {
              animation: "spin 1.5s linear infinite",
              "@keyframes spin": {
                from: {
                  transform: "rotate(0deg)",
                },
                to: {
                  transform: "rotate(360deg)",
                },
              },
            },
          }}
        >
          <Typography
            fontFamily={NumberFont}
            sx={{ color: executing ? "var(--mui-palette-accent-main)" : "text.primary" }}
          >
            {executing ? `Executing ${currentHop}/${totalHops}` : "Resting"}
          </Typography>
          {executing && <DexiSpinner color="var(--mui-palette-accent-main)" />}
        </Stack>
      </Stack>
      {Array.isArray(agentInfo.Tokens) && agentInfo.Tokens.length > 0 && (
        <Stack>
          <Typography variant="caption" color="secondary">
            Tokens
          </Typography>
          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={-2}
            sx={{
              "& > div > *": {
                border: (theme) => `2px solid ${theme.palette.paperAlt}`,
              },
            }}
          >
            {agentInfo.Tokens.map((token) => (
              <Token key={token} tokenId={token} />
            ))}
          </Stack>
        </Stack>
      )}
      {logs.length > 0 && (
        <Stack>
          <Typography variant="caption" color="secondary">
            Logs
          </Typography>
          <Button
            startIcon={<Expand />}
            size="small"
            variant="outlined"
            onClick={() => setOpenLogs(true)}
          >
            See logs
          </Button>
          <Dialog open={openLogs} onClose={() => setOpenLogs(false)} fullWidth maxWidth="md">
            <DialogTitle>Logs</DialogTitle>
            <DialogContent dividers>
              {logs.map((log: any, index: any) => (
                <Typography key={index} variant="body2" component="li">
                  {log}
                </Typography>
              ))}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenLogs(false)}>Close</Button>
            </DialogActions>
          </Dialog>
        </Stack>
      )}
      <Stack>
        <Typography variant="caption" color="secondary">
          Created
        </Typography>
        <Typography fontFamily={NumberFont}>{formatRelative(message.ingestedAt)}</Typography>
      </Stack>
    </>
  )

  if (agentPageView)
    return (
      <Stack direction="row" gap={4}>
        {detailsElement}
      </Stack>
    )

  return (
    <ErrorBoundary key={agentInfo.id} fallback={<>Something went wrong</>}>
      <Stack direction="row" gap={4} marginX={2}>
        <Stack>
          <Typography variant="body1" fontWeight={700}>
            Multi-hop Agent
          </Typography>
          <Typography variant="caption">
            <IdBlock
              color="text.secondary"
              label={truncateId(entityId)}
              value={entityId}
              href={`/entity/${entityId}`}
            />
          </Typography>
        </Stack>

        {detailsElement}
      </Stack>
    </ErrorBoundary>
  )
}
